import { Col, Input, Row } from 'react-dsgov';
import style from './titularArrendatarioFiliais.module.scss';
import { BrButton, BrDivider, BrInput, BrSelect } from '@govbr-ds/react-components';
import { estadosUFOptions } from '../../../../utils/estadosUFLista';
import { useEffect, useState } from 'react';
import { useBackEndRequests } from '../../lib/backEndRequests';
import { useQuery } from '@tanstack/react-query';
import { IMunicipio } from '../../../../interfaces/IProcessos';
import { IFilialLista } from '../../../../interfaces/IIdentificacaoBasica';
import { useNotificacao } from '../../../../hooks/useNotificacao';
import { formatarCNPJOrCpf, unmaskSelectCpfCnpj } from '../../../../utils/formatCNPJOrCPF';
import { validarDocumento } from '../../../../utils/validarDocumento';
import classNames from 'classnames';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface ITitularArrendatarioFiliaisProps {
  declaracaoId: string;
  cnpjMatriz: string;
}

const TitularArrendatarioFiliais = ({ declaracaoId, cnpjMatriz }: ITitularArrendatarioFiliaisProps) => {
  const [estadoUF, setEstadoUF] = useState<string>('');
  const [municipio, setMunicipio] = useState<{
    nome: string;
    codigoIBGE: string;
  }>({
    nome: '',
    codigoIBGE: '',
  });

  const [razaoSocial, setRazaoSocial] = useState<string>('');

  const [ordemCNPJ, setOrdemCNPJ] = useState<string>('');
  const [digitoVerificador, setDigitoVerificador] = useState<string>('');

  const [municipios, setMunicipios] = useState<IMunicipio[]>([]);

  const mostrarNotificacao = useNotificacao();

  const { getMunicipioPorUf, postFilial, getListaFiliais, deleteFilial } = useBackEndRequests();

  const getRaizMatriz = () => {
    return cnpjMatriz.replace(/[^A-Za-z0-9]/g, '').slice(0, 8);
  };

  const { data: municipiosPorUfData } = useQuery<IMunicipio[], IErrorWithSentryId>({
    queryKey: ['municipioPorUfQuery', estadoUF],
    queryFn: () => getMunicipioPorUf(estadoUF),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: !!estadoUF,
  });

  const { data: filiaisData, refetch: refetchLista } = useQuery<IFilialLista[], IErrorWithSentryId>({
    queryKey: ['filiaisDataQuery', declaracaoId],
    queryFn: () => getListaFiliais(declaracaoId),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: !!cnpjMatriz && !!declaracaoId,
  });

  useEffect(() => {
    if (municipiosPorUfData) {
      setMunicipios(municipiosPorUfData);
    }
  }, [municipios, municipiosPorUfData]);

  const formatarRaizCNPJ = (valor: string) => {
    const raizAlfanumerica = valor.slice(0, 8).toUpperCase();
    return `${raizAlfanumerica.slice(0, 2)}.${raizAlfanumerica.slice(2, 5)}.${raizAlfanumerica.slice(5, 8)}`;
  };

  const enviarFilial = async () => {
    const raizMatriz = getRaizMatriz();

    const cnpjCompleto = `${raizMatriz}${ordemCNPJ}${digitoVerificador}`;
    const cnpjCompletoSemFormatacao = cnpjCompleto.replace(/[^A-Za-z0-9]/g, '');

    if (!validarDocumento(cnpjCompletoSemFormatacao)) {
      mostrarNotificacao({
        conteudo: 'CNPJ inválido.',
        tipo: 'danger',
      });
      return;
    }

    const cnpjMatrizSemFormatacao = cnpjMatriz.replace(/[^A-Za-z0-9]/g, '');
    if (cnpjCompletoSemFormatacao === cnpjMatrizSemFormatacao) {
      mostrarNotificacao({
        conteudo: 'Não é permitido cadastrar a matriz como filial.',
        tipo: 'danger',
      });
      return;
    }

    if (filiaisData?.find((filial) => filial.cnpj === cnpjCompletoSemFormatacao.replace(/[.\-/\s]/g, ''))) {
      mostrarNotificacao({
        conteudo: 'CNPJ já listado.',
        tipo: 'danger',
      });
      return;
    }

    if (razaoSocial && municipio.codigoIBGE) {
      const filial = {
        declaracaoId: declaracaoId,
        cnpj: cnpjCompletoSemFormatacao,
        nomeRazaoSocial: razaoSocial,
        codigoIBGE: municipio.codigoIBGE,
      };

      try {
        await postFilial(filial);

        setOrdemCNPJ('');
        setDigitoVerificador('');

        setRazaoSocial('');
        setEstadoUF('');
        setMunicipio({ nome: '', codigoIBGE: '' });

        refetchLista();
      } catch (err) {
        mostrarNotificacao({
          conteudo: 'Ocorreu um erro ao adicionar a Filial. Tente novamente.',
          tipo: 'danger',
          complementoSentryId: err as IErrorWithSentryId,
        });
      }
    } else {
      mostrarNotificacao({
        conteudo: 'Preencha corretamente os campos da Filial.',
        tipo: 'danger',
      });
    }
  };

  const deletarFilial = async (idFilial: string) => {
    await deleteFilial(idFilial)
      .then(() => {
        refetchLista();
      })
      .catch(() => {
        mostrarNotificacao({
          conteudo: 'Ocorreu um erro ao deletar a Filial. Tente novamente.',
          tipo: 'danger',
        });
      });
  };

  return (
    <div className={classNames(style.container, 'TitularArrendatarioFiliais')}>
      <h4 className={style.title}>Filiais</h4>
      <div className={style.form}>
        <Row className={style.row}>
          <Col size={3}>
            <span className={style.label}>CNPJ</span>
            <Row className={classNames(style.row, style.cnpjRow)}>
              <Col size={5}>
                <span className={style.cnpjRaiz}>{formatarRaizCNPJ(getRaizMatriz())}</span>
              </Col>

              <Col size='auto' className={style.cnpjSeparador}>
                /
              </Col>

              <Col size={4} className={style.cnpjOrdem}>
                <BrInput
                  id='input-cnpj-filial'
                  maxLength={4}
                  value={ordemCNPJ}
                  onChange={(e) => setOrdemCNPJ(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())}
                />
              </Col>

              <Col size={3} className={style.cnpjDV}>
                <BrInput
                  id='input-cnpj-sufixo'
                  maxLength={2}
                  value={digitoVerificador}
                  onChange={(e) => setDigitoVerificador(e.target.value.replace(/\D/g, ''))}
                  numeric
                />
              </Col>
            </Row>
          </Col>

          <Col size={3}>
            <span className={style.label}>Razão Social</span>

            <BrInput
              id='input-razao'
              maxLength={200}
              value={razaoSocial}
              onChange={(e) => setRazaoSocial(e.target.value)}
            />
          </Col>
          <Col size={2}>
            <span className={style.label}>UF</span>
            <BrSelect
              id='select-uf'
              key={estadoUF}
              className={style.select}
              options={estadosUFOptions}
              value={estadoUF}
              onChange={(value: string) => {
                setEstadoUF(value);
                setMunicipio({ nome: '', codigoIBGE: '' });
              }}
            />
          </Col>
          <Col size={3}>
            <span className={style.label}>Município</span>
            <BrSelect
              id='select-municipio'
              key={municipio.codigoIBGE}
              className={style.select}
              options={municipios.map((municipio) => ({
                label: municipio.nome,
                value: municipio.codigoIBGE.toString(),
              }))}
              disabled={!estadoUF}
              value={municipio.codigoIBGE.toString()}
              onChange={(value: string) => {
                const selectedMunicipio = municipios.find((m) => m.codigoIBGE.toString() === value);
                if (selectedMunicipio) {
                  setMunicipio(selectedMunicipio);
                }
              }}
            />
          </Col>
          <Col size={1} className={style.buttonContainer}>
            <BrButton id='button-enviar-filial' circle icon='fas fa-plus' secondary onClick={() => enviarFilial()} />
          </Col>
        </Row>
      </div>
      <div className={style.lista}>
        <div className={style.cabecalho}>
          <Row className={style.row}>
            <Col size={3}>
              <span className={style.cabecalhoLabel}>CNPJ</span>
            </Col>
            <Col size={3}>
              <span className={style.cabecalhoLabel}>Razão Social</span>
            </Col>
            <Col size={2}>
              <span className={style.cabecalhoLabel}>UF</span>
            </Col>
            <Col size={3}>
              <span className={style.cabecalhoLabel}>Municipio</span>
            </Col>
            <Col size={1} className={style.acoesColumn}>
              <span className={style.cabecalhoLabel}>Ações</span>
            </Col>
          </Row>
        </div>
        {filiaisData?.length === 0 && <div className={style.emptyText}>Não há Filiais adicionadas</div>}
        {filiaisData?.map((filial) => (
          <div key={filial.cnpj} className={style.linhaFilial}>
            <Row className={style.row}>
              <Col size={3} className={style.infoColumn}>
                {formatarCNPJOrCpf(filial.cnpj)}
              </Col>
              <Col size={3} className={style.infoColumn}>
                {filial.nomeRazaoSocial}
              </Col>
              <Col size={2} className={style.infoColumn}>
                {filial.municipio.uf}
              </Col>
              <Col size={3} className={style.infoColumn}>
                {filial.municipio.nome}
              </Col>
              <Col size={1} className={style.acoesColumn}>
                <BrButton size='small' circle icon='fas fa-trash' onClick={() => deletarFilial(filial.id || '')} />
              </Col>
            </Row>
            <BrDivider />
          </div>
        ))}
      </div>
    </div>
  );
};

export default TitularArrendatarioFiliais;
